import { Title } from '@solidjs/meta'
import { type Component, createMemo, createResource, createSignal, For, Show } from 'solid-js'
import Avatar from '~/components/Avatar'
import Badge from '~/components/Badge'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Form from '~/components/Form'
import Icon from '~/components/Icon'
import Input from '~/components/Input'
import Select from '~/components/Select'
import { useToast } from '~/components/useToast'
import { useAuth } from '~/contexts/AuthContext'
import { useCompanySettings } from '~/hooks/useCompanySettings'
import { authService } from '~/services/auth.service'
import { resolveError } from '~/utils/error'
import { bankAccountTypeOptions } from '~/utils/settings'

export interface UserBillingInfoType {
  name: string
  document: string
  address: string
  phone: string
  email: string
  paymentInfo: {
    bankName: string
    accountType: string
    accountNumber: string
    accountHolder: string
    holderDocument: string
  }
}

const EMPTY_BILLING: UserBillingInfoType = {
  name: '',
  document: '',
  address: '',
  phone: '',
  email: '',
  paymentInfo: { bankName: '', accountType: '', accountNumber: '', accountHolder: '', holderDocument: '' }
}

const BANK_LOGOS: Record<string, { bg: string; short: string }> = {
  bancolombia: { bg: 'from-[#003f9e] to-[#0057b8]', short: 'BC' },
  davivienda: { bg: 'from-[#003a70] to-[#0058a0]', short: 'DV' },
  bbva: { bg: 'from-[#003da5] to-[#1464a5]', short: 'BB' },
  nequi: { bg: 'from-[#8a1ae0] to-[#c22ce5]', short: 'NQ' },
  daviplata: { bg: 'from-[#f59200] to-[#ffb300]', short: 'DP' },
  bancodebogota: { bg: 'from-[#0f7b3e] to-[#2e9e5a]', short: 'BB' },
  avvillas: { bg: 'from-[#d13a44] to-[#e05a63]', short: 'AV' }
}

/** El progreso se calcula sobre los campos que de verdad importan para el PDF. */
const BILLING_FIELDS: Array<{ key: string; label: string }> = [
  { key: 'name', label: 'Razón social / nombre' },
  { key: 'document', label: 'Documento' },
  { key: 'phone', label: 'Teléfono' },
  { key: 'address', label: 'Dirección' },
  { key: 'email', label: 'Correo de facturación' }
]

const BANK_FIELDS: Array<{ key: string; label: string }> = [
  { key: 'bankName', label: 'Banco' },
  { key: 'accountType', label: 'Tipo de cuenta' },
  { key: 'accountNumber', label: 'Número de cuenta' },
  { key: 'accountHolder', label: 'Titular' }
]

const PerfilPage: Component = () => {
  const auth = useAuth()
  const toast = useToast()
  const { settings } = useCompanySettings()

  /** Tipos de cuenta vienen de `companies.settings` (configurables). */
  const bancoOptions = createMemo(() => bankAccountTypeOptions(settings()?.bankAccountTypes ?? []))

  const user = () => (auth.user() ?? {}) as Record<string, unknown>

  const [form, setForm] = createSignal<UserBillingInfoType>(EMPTY_BILLING)
  const [name, setName] = createSignal(String(user().name ?? ''))
  const [document, setDocument] = createSignal(String(user().document ?? ''))
  const [isSaving, setIsSaving] = createSignal(false)
  const [initialized, setInitialized] = createSignal(false)

  /** Carga los datos del usuario actual y deja el formulario listo. */
  const [me] = createResource(async () => {
    try {
      const data = await authService.me()
      const saved = (data?.billingInfo ?? EMPTY_BILLING) as UserBillingInfoType
      setForm({
        ...EMPTY_BILLING,
        ...saved,
        paymentInfo: { ...EMPTY_BILLING.paymentInfo, ...(saved.paymentInfo ?? {}) }
      })
      setName(String(data?.name ?? ''))
      setDocument(String(data?.document ?? ''))
      setInitialized(true)
      return data
    } catch {
      setInitialized(true)
      return null
    }
  })

  const patch = <K extends keyof UserBillingInfoType>(key: K, value: UserBillingInfoType[K]) => setForm(prev => ({ ...prev, [key]: value }))

  const patchPayment = <K extends keyof UserBillingInfoType['paymentInfo']>(key: K, value: string) => setForm(prev => ({ ...prev, paymentInfo: { ...prev.paymentInfo, [key]: value } }))

  const billingCompleted = createMemo(() => {
    const values = form()
    return BILLING_FIELDS.filter(f => String(values[f.key as keyof UserBillingInfoType] ?? '').trim()).length
  })

  const bankCompleted = createMemo(() => {
    const p = form().paymentInfo
    return BANK_FIELDS.filter(f => String(p[f.key as keyof UserBillingInfoType['paymentInfo']] ?? '').trim()).length
  })

  const progress = createMemo(() => {
    const total = BILLING_FIELDS.length + BANK_FIELDS.length
    return Math.round(((billingCompleted() + bankCompleted()) / total) * 100)
  })

  const isComplete = createMemo(() => progress() === 100)

  const bankStyle = createMemo(() => {
    const bank = form().paymentInfo.bankName.toLowerCase()
    const match = Object.keys(BANK_LOGOS).find(k => bank.includes(k))
    return match ? BANK_LOGOS[match] : { bg: 'from-[#d13a44] to-[#e05a63]', short: 'FD' }
  })

  const handleSubmit = async () => {
    setIsSaving(true)
    try {
      await authService.updateMe({
        name: name().trim() || undefined,
        document: document().trim() || undefined,
        billingInfo: form() as unknown as Record<string, unknown>
      })
      toast.addToast({ title: 'Perfil guardado', type: 'success' })
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Container>
      <Title>Mi Perfil | Florez Jaramillo</Title>

      <Show when={me.error}>
        <Card>
          <Card.Body>
            <div class="text-center py-8 text-danger">No se pudo cargar tu perfil: {resolveError(me.error)}</div>
          </Card.Body>
        </Card>
      </Show>

      <Show when={initialized()}>
        <Form id="perfil-form" onSubmit={handleSubmit}>
          {/* Cabecera de identidad: el perfil que verá el cliente en tus cobros. */}
          <div class="rounded-2xl border border-border bg-surface overflow-hidden mb-6">
            <div class="h-24 bg-linear-to-r from-primary-500/80 via-primary-500/50 to-transparent relative z-0">
              <div class="absolute inset-0 opacity-10 flex items-center justify-end pe-8">
                <Icon icon="material-symbols-light:receipt-long-outline" size={120} class="text-white" />
              </div>
            </div>
            <div class="px-6 pb-6 -mt-10 relative z-10">
              <div class="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div class="flex items-end gap-4">
                  <div class="rounded-2xl bg-surface p-1 border border-border shadow-sm">
                    <Avatar alt={form().name || name() || 'Yo'} size={72} class="rounded-xl" />
                  </div>
                  <div class="pb-1">
                    <h1 class="text-2xl font-bold text-foreground leading-tight">{form().name || name() || 'Sin nombre'}</h1>
                    <p class="text-sm text-foreground-subtle flex items-center gap-1.5">
                      <Icon icon="material-symbols-light:mail-outline" size={15} />
                      {String(user().email ?? '')}
                    </p>
                  </div>
                </div>
                <Badge color={isComplete() ? 'success' : 'warning'}>{isComplete() ? 'Perfil completo' : `${progress()}% completado`}</Badge>
              </div>

              {/* Progreso de completitud del perfil. */}
              <div class="mt-5">
                <div class="flex justify-between text-xs text-foreground-subtle mb-1.5">
                  <span>Datos de facturación</span>
                  <span>
                    {billingCompleted()}/{BILLING_FIELDS.length} · Banco {bankCompleted()}/{BANK_FIELDS.length}
                  </span>
                </div>
                <div class="h-2 rounded-full bg-surface-muted overflow-hidden">
                  <div class="h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${progress()}%` }} />
                </div>
              </div>
            </div>
          </div>

          <div class="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
            {/* Columna principal: datos de identidad y de facturación. */}
            <div class="xl:col-span-2 space-y-6">
              <Card>
                <Card.Header>
                  <div class="flex items-center gap-2">
                    <div class="size-8 rounded-lg bg-primary-soft text-primary flex items-center justify-center">
                      <Icon icon="material-symbols-light:person-outline" size={18} />
                    </div>
                    <div>
                      <h2 class="font-semibold text-foreground">Tu cuenta</h2>
                      <p class="text-xs text-foreground-subtle">Cómo te identificas al entrar a la plataforma</p>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body>
                  <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <Input label="Nombre" value={name()} onInput={e => setName(e.currentTarget.value)} />
                    <Input label="Documento (usuario)" placeholder="NIT, cédula..." value={document()} onInput={e => setDocument(e.currentTarget.value)} />
                    <Input label="Correo" type="email" value={String(user().email ?? '')} disabled helperText="El correo no se puede cambiar aquí" />
                  </div>
                </Card.Body>
              </Card>

              <Card>
                <Card.Header>
                  <div class="flex items-center gap-2">
                    <div class="size-8 rounded-lg bg-info-soft text-info flex items-center justify-center">
                      <Icon icon="material-symbols-light:assignment-ind-outline" size={18} />
                    </div>
                    <div>
                      <h2 class="font-semibold text-foreground">Facturación a tu nombre</h2>
                      <p class="text-xs text-foreground-subtle">Estos datos aparecen como emisor cuando cobras a tu nombre</p>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body>
                  <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <Input label="Razón social / nombre" placeholder={name() || 'Tu nombre o empresa'} value={form().name} onInput={e => patch('name', e.currentTarget.value)} />
                    <Input label="Documento" value={form().document} onInput={e => patch('document', e.currentTarget.value)} />
                    <Input label="Teléfono" value={form().phone} onInput={e => patch('phone', e.currentTarget.value)} />
                    <Input label="Dirección" value={form().address} onInput={e => patch('address', e.currentTarget.value)} />
                    <Input label="Correo de facturación" type="email" class="lg:col-span-2" value={form().email} onInput={e => patch('email', e.currentTarget.value)} />
                  </div>
                </Card.Body>
              </Card>
            </div>

            {/* Columna derecha: la vista previa de "cómo saldrá tu emisor". */}
            <div class="space-y-6 xl:sticky xl:top-4">
              <Card color="primary" style="border">
                <Card.Header>
                  <div class="flex items-center justify-between">
                    <h2 class="font-semibold text-foreground">Así saldrá tu emisor</h2>
                    <Icon icon="material-symbols-light:picture-as-pdf-outline" size={20} class="text-foreground-subtle" />
                  </div>
                </Card.Header>
                <Card.Body>
                  <p class="text-xs text-foreground-subtle mb-4">Vista previa de la tarjeta "EMITE" que aparece en el PDF cuando la cuenta de cobro es a tu nombre.</p>

                  <div class="rounded-xl border border-border bg-surface-container/60 p-4 space-y-3">
                    <div class="flex items-center gap-3">
                      <Avatar alt={form().name || name() || 'Yo'} size={40} class="rounded-full" />
                      <div class="min-w-0">
                        <p class="font-semibold text-foreground truncate">{form().name || name() || '—'}</p>
                        <p class="text-xs text-foreground-subtle truncate">EMITE · cuenta de cobro</p>
                      </div>
                    </div>
                    <div class="h-px bg-border" />
                    <dl class="space-y-2 text-xs">
                      <For each={BILLING_FIELDS}>
                        {f => {
                          const value = String(form()[f.key as keyof UserBillingInfoType] ?? '').trim()
                          return (
                            <div class="flex justify-between gap-3">
                              <dt class="text-foreground-subtle shrink-0">{f.label}</dt>
                              <dd class={value ? 'text-foreground font-medium text-right' : 'text-foreground-subtle/60 italic text-right'}>{value || '—'}</dd>
                            </div>
                          )
                        }}
                      </For>
                    </dl>
                  </div>
                </Card.Body>
              </Card>
            </div>
          </div>

          {/* Cuenta bancaria con apariencia de tarjeta de crédito. */}
          <Card class="mt-6">
            <Card.Header>
              <div class="flex items-center gap-2">
                <div class="size-8 rounded-lg bg-success-soft text-success flex items-center justify-center">
                  <Icon icon="material-symbols-light:account-balance-outline" size={18} />
                </div>
                <div>
                  <h2 class="font-semibold text-foreground">Cuenta bancaria</h2>
                  <p class="text-xs text-foreground-subtle">Dónde recibes el pago cuando cobras a tu nombre</p>
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              <div class="grid grid-cols-1 xl:grid-cols-2 gap-6">
                {/* La tarjeta visual: solo se ve "real" cuando hay datos que mostrar. */}
                <div class="flex items-center">
                  <div class="w-full max-w-sm rounded-2xl bg-linear-to-br text-white p-6 shadow-lg relative overflow-hidden" classList={{ [bankStyle().bg]: true }}>
                    <div class="absolute -top-10 -right-10 size-40 rounded-full bg-white/10" />
                    <div class="absolute -bottom-12 -right-4 size-48 rounded-full bg-white/10" />
                    <div class="relative">
                      <div class="flex items-center justify-between mb-8">
                        <span class="font-bold tracking-wide text-white/90">{form().paymentInfo.bankName || 'Tu banco'}</span>
                        <span class="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider bg-white/15 rounded-lg px-2 py-1">
                          <Icon icon="material-symbols-light:key-outline" size={13} />
                          {form().paymentInfo.accountType || 'Cuenta'}
                        </span>
                      </div>
                      <p class="text-lg font-mono tracking-widest mb-6">{formatAccountNumber(form().paymentInfo.accountNumber)}</p>
                      <div class="flex items-end justify-between">
                        <div>
                          <p class="text-[10px] uppercase tracking-widest text-white/70 mb-0.5">Titular</p>
                          <p class="text-sm font-semibold truncate max-w-40">{form().paymentInfo.accountHolder || '—'}</p>
                        </div>
                        <div class="text-right">
                          <p class="text-[10px] uppercase tracking-widest text-white/70 mb-0.5">Doc</p>
                          <p class="text-sm font-semibold">{form().paymentInfo.holderDocument || '—'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Los campos editables. */}
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <Input label="Banco" placeholder="Bancolombia, Davivienda..." value={form().paymentInfo.bankName} onInput={e => patchPayment('bankName', e.currentTarget.value)} />
                  <Select
                    label="Tipo de cuenta"
                    value={form().paymentInfo.accountType}
                    onChange={v => patchPayment('accountType', String(v ?? ''))}
                    placeholder="Selecciona..."
                    clearable
                    options={bancoOptions()}
                  />
                  <Input label="Número de cuenta" placeholder="000-123456-78" value={form().paymentInfo.accountNumber} onInput={e => patchPayment('accountNumber', e.currentTarget.value)} />
                  <Input
                    label="Titular de la cuenta"
                    placeholder="Nombre o razón social"
                    value={form().paymentInfo.accountHolder}
                    onInput={e => patchPayment('accountHolder', e.currentTarget.value)}
                  />
                  <Input
                    label="Documento del titular"
                    placeholder="NIT, cédula..."
                    class="lg:col-span-2"
                    value={form().paymentInfo.holderDocument}
                    onInput={e => patchPayment('holderDocument', e.currentTarget.value)}
                  />
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Acciones al fondo, fuera de las tarjetas. */}
          <div class="flex justify-end mt-6 space-x-4">
            <Button type="button" color="default" variant="soft" onClick={() => window.history.back()}>
              Cancelar
            </Button>
            <Button type="submit" color="primary" disabled={isSaving() || me.loading}>
              {isSaving() ? 'Guardando...' : 'Guardar Perfil'}
            </Button>
          </div>
        </Form>
      </Show>
    </Container>
  )
}

/** 1234567890 → 1234 5678 90 (agrupa el número para que se lea mejor). */
function formatAccountNumber(value: string): string {
  const digits = value.replace(/\D/g, '')
  if (!digits) return '•••• •••• ••••'
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim()
}

export default PerfilPage
